<template>
  <b-modal v-model="active" :id="modalId">
    <div slot="modal-header" minimize class="app-modal-header">
      <span class="app-modal-header__icon"><AppIcon name="lock" /></span>
      <h5 class="app-modal-header__title">Bloqueo mensual</h5>
      <AccountingBookBadge :declared="declared" class="ml-2" />
    </div>

    <div v-if="status">
      <p class="small text-muted">
        Al bloquear un mes terminado (por ejemplo, al presentar el IVA) no se pueden cargar, editar ni borrar comprobantes ni asientos de esas fechas, y sus asientos toman el número definitivo del Libro Diario.
      </p>

      <div class="lock-state">
        <AppIcon :name="status.locked_until ? 'lock' : 'unlock'" class="mr-2" />
        <span v-if="status.locked_until">Bloqueado hasta el <b>{{ formatDate(status.locked_until) }}</b></span>
        <span v-else class="text-muted">Sin meses bloqueados</span>
      </div>

      <b-alert show variant="info" class="small mt-3 mb-0" v-if="!status.period_start">Este libro todavía no tiene apertura: primero cargá la apertura inicial.</b-alert>

      <template v-else>
        <div class="lock-row mt-3">
          <b-form-group label="Bloquear hasta el final de" class="mb-0">
            <b-form-input type="month" size="sm" v-model="month" :max="maxMonth"></b-form-input>
          </b-form-group>
          <b-button variant="dark" size="sm" :disabled="loading || !month" @click="lock()"><AppIcon name="lock" class="mr-1" />Bloquear</b-button>
        </div>
        <small class="text-muted d-block mt-1">Requiere cero asientos pendientes hasta esa fecha. Si llevás inventario mensual, generalo antes.</small>

        <div class="lock-unlock mt-3" v-if="status.locked_until && canUnlock">
          <b-button variant="link" size="sm" class="px-0 text-danger" v-if="!unlocking" :disabled="!!status.unlock_block" @click="unlocking = true">
            <AppIcon name="unlock" class="mr-1" />Desbloquear {{ monthName(status.locked_until) }}
          </b-button>
          <small class="text-muted d-block" v-if="status.unlock_block">{{ status.unlock_block }}</small>
          <div v-if="unlocking">
            <b-form-group label="Motivo del desbloqueo *" class="mb-2">
              <b-form-textarea v-model="reason" rows="2" placeholder="Por qué se desbloquea el mes" />
            </b-form-group>
            <b-button variant="outline-secondary" size="sm" class="mr-1" @click="unlocking = false">Cancelar</b-button>
            <b-button variant="danger" size="sm" :disabled="loading || !reason.trim()" @click="unlock()"><AppIcon name="unlock" class="mr-1" />Desbloquear</b-button>
          </div>
        </div>
      </template>

      <div class="lock-history" v-if="status.month_locks.length">
        <small class="lock-history__label">Últimos cambios</small>
        <div v-for="item in status.month_locks" :key="item.id" class="small">
          {{ moment(item.created_at).format('DD/MM/YYYY HH:mm') }} · {{ item.reason ? 'Desbloqueo' : 'Bloqueo' }}: {{ item.locked_until ? 'hasta ' + formatDate(item.locked_until) : 'sin meses bloqueados' }}<span v-if="item.user_name"> · {{ item.user_name }}</span><span v-if="item.reason"> · {{ item.reason }}</span>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-3"><b-spinner small /></div>

    <div slot="modal-footer">
      <b-button variant="outline-secondary" size="sm" @click="active = false"><AppIcon name="close" class="mr-1" />Cerrar</b-button>
    </div>
  </b-modal>
</template>
<script>
  import serviceAPI from '../services'
  import Error from '@/handler/error'
  import moment from 'moment'
  import AccountingBookBadge from './AccountingBookBadge'

  // Bloqueo y desbloqueo de meses de un libro.
  export default {
    components: {
      AccountingBookBadge,
    },
    props: {
      businessId: {
        default: null,
      },
      declared: {
        type: Boolean,
        default: true,
      },
      // El desbloqueo lo restringe el permiso reopen_period de Cierre y Apertura; el backend lo vuelve a validar
      canUnlock: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        active: false,
        modalId: 'month_lock_' + Math.random().toString(36).slice(2, 8),
        status: null,
        month: '',
        maxMonth: moment().subtract(1, 'month').format('YYYY-MM'),
        unlocking: false,
        reason: '',
        loading: false,
      }
    },
    methods: {
      moment,
      open() {
        this.status = null
        this.unlocking = false
        this.reason = ''
        this.month = this.maxMonth
        this.active = true
        this.load()
      },
      load() {
        return serviceAPI.obtenerEstadoPeriodo({ business_id: this.businessId, declared: this.declared })
        .then(response => {
          this.status = response.data
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      lock() {
        this.loading = true
        serviceAPI.bloquearMes({ business_id: this.businessId, declared: this.declared, month: this.month })
        .then(() => {
          this.$awn.success('Mes bloqueado hasta el final de ' + this.monthName(this.month + '-01'))
          this.$emit('changed')
          return this.load()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      unlock() {
        this.loading = true
        serviceAPI.desbloquearMes({ business_id: this.businessId, declared: this.declared, reason: this.reason })
        .then(() => {
          this.$awn.success('Mes desbloqueado')
          this.unlocking = false
          this.reason = ''
          this.$emit('changed')
          return this.load()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      monthName(date) {
        return moment(date).locale('es').format('MMMM YYYY')
      },
      formatDate(date) {
        return date ? moment(date).format('DD/MM/YYYY') : ''
      },
    },
  }
</script>
<style scoped>
  .lock-state {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
  }
  .lock-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 8px;
  }
  .lock-history {
    margin-top: 16px;
    padding-top: 8px;
    border-top: 1px solid var(--app-color-border);
  }
  .lock-history__label {
    display: block;
    font-weight: 600;
    color: var(--app-color-text-secondary);
  }
</style>
